import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import {
  PhCaretDown,
  PhCaretUp,
  PhArrowRight,
  PhSparkle,
  PhBookOpen,
  PhLink,
  PhInfo,
} from 'ember-phosphor-icons';

/**
 * Glossary term category for filtering and visual styling
 */
export type GlossaryCategory = 'loans' | 'repayment' | 'forgiveness' | 'financial' | 'general';

/**
 * Glossary term data structure
 */
export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  category: GlossaryCategory;
  relatedTerms?: string[];
  relatedRoute?: string;
  relatedRouteText?: string;
  examples?: string[];
  tip?: string;
}

/**
 * GlossaryTermCard - Interactive glossary term card with expand/collapse
 *
 * Displays a glossary term with definition, category badge, related links,
 * and optional AI explanation integration.
 *
 * @example
 * ```gts
 * <GlossaryTermCard
 *   @term={{this.term}}
 *   @onAskAI={{this.askAboutTerm}}
 * />
 * ```
 */
interface GlossaryTermCardSignature {
  Args: {
    /** The glossary term data */
    term: GlossaryTerm;
    /** Handler for "Ask AI" button click */
    onAskAI?: (term: GlossaryTerm) => void;
    /** Whether to start expanded */
    expanded?: boolean;
    /** Callback when expand state changes */
    onToggle?: (termId: string, expanded: boolean) => void;
  };
  Element: HTMLDivElement;
}

const categoryStyles: Record<GlossaryCategory, { bg: string; text: string; border: string }> = {
  loans: {
    bg: 'tio-bg-info-soft',
    text: 'tio-fg-info-body',
    border: 'tio-border-info',
  },
  repayment: {
    bg: 'tio-bg-success-soft',
    text: 'tio-fg-success-body',
    border: 'tio-border-success',
  },
  forgiveness: {
    bg: 'tio-bg-brand-soft',
    text: 'tio-fg-brand',
    border: 'tio-border-brand',
  },
  financial: {
    bg: 'tio-bg-warning-soft',
    text: 'tio-fg-warning-body',
    border: 'tio-border-warning',
  },
  general: {
    bg: 'tio-bg-sunken',
    text: 'tio-fg-secondary',
    border: 'tio-border-strong',
  },
};

const getCategoryLabel = (category: GlossaryCategory): string => {
  const labels: Record<GlossaryCategory, string> = {
    loans: 'Loans',
    repayment: 'Repayment',
    forgiveness: 'Forgiveness',
    financial: 'Financial',
    general: 'General',
  };
  return labels[category];
};

export default class GlossaryTermCard extends Component<GlossaryTermCardSignature> {
  @tracked isExpanded = this.args.expanded ?? false;

  get categoryStyle() {
    return categoryStyles[this.args.term.category];
  }

  get categoryLabel() {
    return getCategoryLabel(this.args.term.category);
  }

  get hasExtendedContent() {
    const term = this.args.term;
    return (
      (term.relatedTerms && term.relatedTerms.length > 0) ||
      term.relatedRoute ||
      (term.examples && term.examples.length > 0) ||
      term.tip
    );
  }

  toggle = () => {
    this.isExpanded = !this.isExpanded;
    this.args.onToggle?.(this.args.term.id, this.isExpanded);
  };

  askAI = () => {
    this.args.onAskAI?.(this.args.term);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Card @padding="none" class="tio-glossary-card {{this.categoryStyle.border}}" ...attributes>
      {{! Header - always visible }}
      <button
        type="button"
        class="tio-glossary-card__toggle"
        {{on "click" this.toggle}}
        aria-expanded={{if this.isExpanded "true" "false"}}
      >
        <div class="tio-glossary-card__head">
          <div class="tio-glossary-card__title-row">
            <Heading @level={{4}}>
              {{@term.term}}
            </Heading>
            <Chip class="text-xs {{this.categoryStyle.bg}} {{this.categoryStyle.text}}">
              {{this.categoryLabel}}
            </Chip>
          </div>
          <Text
            @variant="inherit"
            @size="sm"
            class="tio-glossary-card__def {{unless this.isExpanded 'line-clamp-2'}}"
          >
            {{@term.definition}}
          </Text>
        </div>

        <div class="tio-glossary-card__toggle-end">
          {{#if this.isExpanded}}
            <PhCaretUp @size={{20}} class="tio-glossary-card__caret" />
          {{else}}
            <PhCaretDown @size={{20}} class="tio-glossary-card__caret" />
          {{/if}}
        </div>
      </button>

      {{! Expanded content }}
      {{#if this.isExpanded}}
        <div class="tio-glossary-card__body">
          {{! Examples }}
          {{#if @term.examples}}
            <div>
              <div class="tio-glossary-card__section-head">
                <PhInfo @size={{16}} class="tio-fg-info" />
                <Text @variant="label" @size="sm">
                  Examples
                </Text>
              </div>
              <ul class="tio-glossary-card__list">
                {{#each @term.examples as |example|}}
                  <li>{{example}}</li>
                {{/each}}
              </ul>
            </div>
          {{/if}}

          {{! Tip }}
          {{#if @term.tip}}
            <div class="tio-glossary-card__tip tio-bg-warning-soft tio-border-warning">
              <div class="tio-glossary-card__tip-inner">
                <PhSparkle @size={{16}} class="tio-fg-warning shrink-0 mt-0.5" />
                <div>
                  <Text
                    @variant="inherit"
                    @size="sm"
                    @weight="medium"
                    class="tio-fg-warning-h mb-1"
                  >
                    Pro Tip
                  </Text>
                  <Text @variant="inherit" @size="sm" class="tio-fg-warning-body">
                    {{@term.tip}}
                  </Text>
                </div>
              </div>
            </div>
          {{/if}}

          {{! Related Terms }}
          {{#if @term.relatedTerms}}
            <div>
              <div class="tio-glossary-card__section-head">
                <PhLink @size={{16}} class="tio-fg-brand-accent" />
                <Text @variant="label" @size="sm">
                  Related Terms
                </Text>
              </div>
              <div class="tio-glossary-card__chips">
                {{#each @term.relatedTerms as |related|}}
                  <Chip
                    class="text-xs tio-bg-brand-soft hover:tio-bg-brand-soft-hover tio-fg-brand cursor-pointer transition-colors"
                  >
                    {{related}}
                  </Chip>
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{! Actions }}
          <div class="tio-glossary-card__actions">
            {{#if @term.relatedRoute}}
              <LinkTo @route={{@term.relatedRoute}} class="tio-glossary-card__link tio-fg-brand">
                <PhBookOpen @size={{16}} />
                {{#if @term.relatedRouteText}}
                  {{@term.relatedRouteText}}
                {{else}}
                  Learn More
                {{/if}}
                <PhArrowRight @size={{14}} />
              </LinkTo>
            {{/if}}

            {{#if @onAskAI}}
              <Button
                @intent="primary"
                @appearance="minimal"
                class="text-sm"
                {{on "click" this.askAI}}
              >
                <PhSparkle @size={{16}} class="mr-1" />
                Ask AI for More
              </Button>
            {{/if}}
          </div>
        </div>
      {{/if}}
    </Card>
  </template>
}
